import React from 'react';
import { Box, Button, Card, CardContent, Typography } from '@mui/material';
import { ActivityRequest, JobType, BOOKING_TYPES, TARGET_STATUSES } from 'us-web-services';

export interface Preset {
  name: string;
  description: string;
  config: Partial<ActivityRequest>;
}

export const PRESETS: Preset[] = [
  {
    name: 'Quick Book (Cash)',
    description: 'One-time childcare booking with cash payment',
    config: {
      activityScope: 'FULL_FLOW',
      jobType: JobType.ONE_TIME,
      bookingTypeId: BOOKING_TYPES.CHILDCARE,
      paymentType: 'CASH',
      targetBookingStatus: TARGET_STATUSES.ACCEPTED,
    },
  },
  {
    name: 'Quick Book (Credit)',
    description: 'One-time childcare booking with credit card',
    config: {
      activityScope: 'FULL_FLOW',
      jobType: JobType.ONE_TIME,
      bookingTypeId: BOOKING_TYPES.CHILDCARE,
      paymentType: 'CREDIT',
      targetBookingStatus: TARGET_STATUSES.ACCEPTED,
    },
  },
  {
    name: 'B2B Care Credit',
    description: 'One-time booking using company care credit',
    config: {
      activityScope: 'FULL_FLOW',
      jobType: JobType.ONE_TIME,
      bookingTypeId: BOOKING_TYPES.CHILDCARE,
      paymentType: 'CARE_CREDIT',
      targetBookingStatus: TARGET_STATUSES.ACCEPTED,
    },
  },
  {
    name: 'Pending Booking',
    description: 'Booking left in pending state',
    config: {
      activityScope: 'FULL_FLOW',
      jobType: JobType.ONE_TIME,
      bookingTypeId: BOOKING_TYPES.CHILDCARE,
      paymentType: 'CASH',
      targetBookingStatus: TARGET_STATUSES.PENDING,
    },
  },
  {
    name: 'Completed',
    description: 'Completed booking',
    config: {
      activityScope: 'FULL_FLOW',
      jobType: JobType.ONE_TIME,
      bookingTypeId: BOOKING_TYPES.CHILDCARE,
      paymentType: 'CREDIT',
      targetBookingStatus: TARGET_STATUSES.COMPLETED,
      timeOffset: 'PAST',
    },
  },
  {
    name: 'Job Post Only',
    description: 'Create job post without sitter interaction',
    config: {
      activityScope: 'JOB_ONLY',
      jobType: JobType.ONE_TIME,
      bookingTypeId: BOOKING_TYPES.CHILDCARE,
      paymentType: 'CASH',
    },
  },
  {
    name: 'Sitter Interest Only',
    description: 'Job with sitter interest but no booking',
    config: {
      activityScope: 'JOB_INTEREST',
      jobType: JobType.ONE_TIME,
      bookingTypeId: BOOKING_TYPES.CHILDCARE,
      paymentType: 'CASH',
    },
  },
  {
    name: 'Recurring PT Job',
    description: 'Part-time recurring job, 3 days/week',
    config: {
      activityScope: 'FULL_FLOW',
      jobType: JobType.RECURRING,
      bookingTypeId: BOOKING_TYPES.CHILDCARE,
      paymentType: 'CREDIT',
      recurringDays: ['MONDAY', 'WEDNESDAY', 'FRIDAY'],
    },
  },
];

interface ActivityPresetsCardProps {
  onApplyPreset: (preset: Preset) => void;
}

const ActivityPresetsCard: React.FC<ActivityPresetsCardProps> = ({ onApplyPreset }) => (
  <Card>
    <CardContent>
      <Typography variant='h6' gutterBottom>
        Presets
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {PRESETS.map((preset) => (
          <Button
            key={preset.name}
            variant='outlined'
            size='small'
            onClick={() => onApplyPreset(preset)}
            title={preset.description}
          >
            {preset.name}
          </Button>
        ))}
      </Box>
    </CardContent>
  </Card>
);

export default ActivityPresetsCard;
